// src/components/SupervisorMessaging.js
// Used inside SupervisorDashboard to send messages to technicians
import React, { useState } from 'react';
import {
  notifySupervisorMessage,
  notifyAnnouncement,
  notifyServiceRejected
} from '../utils/NotificationService';
import './SupervisorMessaging.css';

function SupervisorMessaging({ user, technicians }) {
  const [mode, setMode]         = useState('message'); // 'message' | 'announce' | 'reject'
  const [recipient, setRecipient] = useState('');
  const [message, setMessage]   = useState('');
  const [orderNum, setOrderNum] = useState('');
  const [outletName, setOutletName] = useState('');
  const [reason, setReason]     = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);

  const selectedTech = technicians.find(t => t.id === recipient);

  const handleSend = async () => {
    setSending(true);
    try {
      if (mode === 'message') {
        if (!recipient || !message.trim()) return;
        await notifySupervisorMessage(
          recipient,
          selectedTech?.name || '',
          user.name,
          message.trim()
        );
      } else if (mode === 'announce') {
        if (!message.trim()) return;
        await notifyAnnouncement(technicians, user.name, message.trim());
      } else if (mode === 'reject') {
        if (!recipient || !reason.trim()) return;
        await notifyServiceRejected(
          recipient,
          selectedTech?.name || '',
          orderNum.trim(),
          outletName.trim(),
          reason.trim(),
          user.name
        );
      }

      setSent(true);
      setMessage('');
      setReason('');
      setOrderNum('');
      setOutletName('');
      setTimeout(() => setSent(false), 3000);
    } catch (e) {
      alert('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const canSend = () => {
    if (sending) return false;
    if (mode === 'message')  return recipient && message.trim();
    if (mode === 'announce') return message.trim();
    if (mode === 'reject')   return recipient && reason.trim();
    return false;
  };

  return (
    <div className="sm-container">
      <div className="sm-section-header">
        <h2 className="sm-title">📨 Send to Technicians</h2>
        <p className="sm-sub">Direct message, announcement, or service correction</p>
      </div>

      {/* Mode tabs */}
      <div className="sm-mode-tabs">
        {[
          { key: 'message',  label: '💬 Direct Message' },
          { key: 'announce', label: '📢 Broadcast All'  },
          { key: 'reject',   label: '❌ Service Correction' }
        ].map(m => (
          <button
            key={m.key}
            className={`sm-mode-tab ${mode === m.key ? 'active' : ''}`}
            onClick={() => { setMode(m.key); setSent(false); }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="sm-form">
        {/* Recipient selector — message + reject modes */}
        {(mode === 'message' || mode === 'reject') && (
          <div className="sm-field">
            <label className="sm-label">Technician *</label>
            <select
              className="sm-select"
              value={recipient}
              onChange={e => setRecipient(e.target.value)}
            >
              <option value="">— Select technician —</option>
              {technicians.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Broadcast label */}
        {mode === 'announce' && (
          <div className="sm-broadcast-banner">
            📢 This message will be sent to all <strong>{technicians.length}</strong> technicians
          </div>
        )}

        {/* Service rejection fields */}
        {mode === 'reject' && (
          <>
            <div className="sm-field">
              <label className="sm-label">Service Order # (optional)</label>
              <input
                className="sm-input"
                placeholder="e.g. 042"
                value={orderNum}
                onChange={e => setOrderNum(e.target.value)}
              />
            </div>
            <div className="sm-field">
              <label className="sm-label">Outlet Name (optional)</label>
              <input
                className="sm-input"
                placeholder="e.g. Kona Bar"
                value={outletName}
                onChange={e => setOutletName(e.target.value)}
              />
            </div>
            <div className="sm-field">
              <label className="sm-label">Reason for Correction *</label>
              <textarea
                className="sm-input textarea-sm"
                placeholder="Explain what needs to be corrected..."
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </div>
          </>
        )}

        {/* Message textarea — message + announce modes */}
        {(mode === 'message' || mode === 'announce') && (
          <div className="sm-field">
            <label className="sm-label">
              {mode === 'message' ? 'Message *' : 'Announcement *'}
            </label>
            <textarea
              className="sm-input textarea"
              placeholder={
                mode === 'message'
                  ? 'Type your message to this technician...'
                  : 'Type your announcement to all technicians...'
              }
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
            <div className="sm-char-count">{message.length} characters</div>
          </div>
        )}

        {/* Send button */}
        <button
          className={`sm-send-btn ${sent ? 'sent' : ''}`}
          onClick={handleSend}
          disabled={!canSend()}
        >
          {sending ? '⏳ Sending...' : sent ? '✅ Sent!' : (
            mode === 'message'  ? '📨 Send Message' :
            mode === 'announce' ? '📢 Broadcast to All' :
            '❌ Send Correction Notice'
          )}
        </button>
      </div>
    </div>
  );
}

export default SupervisorMessaging;
